import { Stack, Tooltip, Typography } from '@mui/material';
import DOMPurify from 'dompurify';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import {
  getActionNameTranslation,
  getCollectionLabelIcon,
  getCollectionLabelTranslation,
  getHighlightFieldLabelTranslation,
} from './helpers';
import styles from './SearchResultItem.module.scss';
import type {
  ActionDocument,
  CustomerDocument,
  EmployeeDocument,
  FormattedSearchResult,
  ProjectDocument,
} from '@/api/typesense/search';
import { Icon } from '@/components/ui/Icon';
import { uiIcons } from '@/constants/uiIcons';
import { useHasNewProjectManagement } from '@/hooks/featureFlags';
import { stringToPascal } from '@/legacy/utils/string';
import { useNewProjectModalStore } from '@/stores/newProjectModal';

interface SearchResultItemProps {
  result: FormattedSearchResult;
  resultId: string;
  isActive: boolean;
  onItemSelect: (
    item: FormattedSearchResult,
    shouldCloseModal: boolean,
  ) => void;
}

const getAdditionalNameInfo = (result: FormattedSearchResult) => {
  if (
    result.documentType === 'projects' ||
    result.documentType === 'customers'
  ) {
    const number = (result as CustomerDocument | ProjectDocument).Number;
    return number ? ` (${number})` : '';
  }

  if (result.documentType === 'employees') {
    const initials = (result as EmployeeDocument).Initials;
    return initials ? ` (${initials})` : '';
  }

  return '';
};

export const SearchResultItem = ({
  result,
  resultId,
  isActive,
  onItemSelect,
}: SearchResultItemProps) => {
  const { t, i18n } = useTranslation('searchModal');
  const hasNewProjectManagement = useHasNewProjectManagement();
  const openNewProjectModal = useNewProjectModalStore((state) => state.open);
  const automationId = `SearchResult${stringToPascal(result.documentType)}${result.documentType === 'actions' ? stringToPascal(result.Name) : ''}`;
  const additionalNameInfo = getAdditionalNameInfo(result);
  const isCreateProjectAction =
    result.documentType === 'actions' &&
    (result as ActionDocument).ActionKey === 'action-create-project';

  return (
    <Link
      className={styles.SearchResultItem}
      id={resultId}
      to={result.pageUrl}
      role="option"
      aria-selected={isActive}
      onClick={(e) => {
        // Don't close modal if opening in new tab (Ctrl/Cmd/Shift+Click)
        const shouldCloseModal = !(e.ctrlKey || e.metaKey || e.shiftKey);
        if (
          shouldCloseModal &&
          hasNewProjectManagement &&
          isCreateProjectAction
        ) {
          e.preventDefault();
          onItemSelect(result, shouldCloseModal);
          openNewProjectModal();
          return;
        }
        onItemSelect(result, shouldCloseModal);
      }}
      data-automation-id={automationId}
    >
      <Stack>
        <Stack direction="row" alignItems="start" spacing={3}>
          <Tooltip
            title={getCollectionLabelTranslation(result.documentType, i18n, t)}
            disableTouchListener
            disableFocusListener
          >
            <div className={styles.SearchResultItemIconContainer}>
              <Icon
                className={styles.InfoIcon}
                name={getCollectionLabelIcon(result.documentType)}
              />
            </div>
          </Tooltip>

          <div className={styles.SearchResultItemNameContainer}>
            {result.documentType === 'actions' ? (
              <span
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(
                    result.Name || (result as ActionDocument).ActionKey
                      ? getActionNameTranslation(
                          (result as ActionDocument).ActionKey,
                          result.Name,
                          i18n,
                          t,
                        )
                      : 'Missing Name',
                  ),
                }}
              />
            ) : (
              <span
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(
                    result.Name
                      ? result.Name + additionalNameInfo
                      : t('NoNameLabel'),
                  ),
                }}
              />
            )}

            {result.highlightedProperties?.length &&
            result.highlightedProperties.some(
              (highlight) =>
                highlight !== 'Name' &&
                highlight !== 'FirstName' &&
                highlight !== 'LastName',
            ) ? (
              <div className={styles.HighlightedPropertiesGrid}>
                {result.highlightedProperties
                  ?.filter(
                    (highlight) =>
                      highlight !== 'Name' &&
                      highlight !== 'FirstName' &&
                      highlight !== 'LastName',
                  )
                  ?.map((highlight, index) => (
                    <Typography key={`${index}_${highlight}`} variant="label">
                      <span>
                        {getHighlightFieldLabelTranslation(highlight, i18n, t)}
                      </span>
                      <span
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(
                            String(result[highlight] ?? ''),
                          ),
                        }}
                      />
                    </Typography>
                  ))}
              </div>
            ) : null}
          </div>

          <Tooltip
            title={t('PressEnterToOpen')}
            disableTouchListener
            disableFocusListener
          >
            <div
              className={`${styles.SearchResultItemIconContainer} ${
                styles.SearchResultItemReturnIcon
              }`}
            >
              <Icon name={uiIcons.keys.enter} />
            </div>
          </Tooltip>
        </Stack>
      </Stack>
    </Link>
  );
};
